import { useMemo, useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronUp, Nfc, X } from 'lucide-react'
import { BRAND } from '@/constants/brand'
import { cn } from '@/utils/cn'

export type LineItemCardBayOption = { value: string; label: string }

export type LineScanStatus = 'pending' | 'in_progress' | 'complete'

export type LineItemCardProps = {
  materialCode: string
  materialDesc?: string
  requiredQty: number
  scanQty: number
  nonScanQty: number
  status?: LineScanStatus
  scannable?: boolean
  fromBay?: string
  fromBin?: string
  toBay?: string
  showFromBay?: boolean
  showFromBin?: boolean
  showToBay?: boolean
  showShortQty?: boolean
  shortQty?: number | string
  bayOptions?: LineItemCardBayOption[]
  binOptions?: LineItemCardBayOption[]
  onNonScanChange?: (value: string) => void
  onFromBayChange?: (value: string) => void
  onFromBinChange?: (value: string) => void
  onToBayChange?: (value: string) => void
  onShortQtyChange?: (value: string) => void
  /** Allocated RFID tags for this line */
  tags?: string[]
  onRemoveTag?: (tag: string) => void
  selected?: boolean
  onSelect?: () => void
  className?: string
}

const STATUS_META: Record<
  LineScanStatus,
  { label: string; className: string }
> = {
  pending: {
    label: 'Pending',
    className: 'bg-muted text-muted-foreground border-border',
  },
  in_progress: {
    label: 'In progress',
    className: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  complete: {
    label: 'Complete',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
}

function QtyCell({
  label,
  value,
  accent,
}: {
  label: string
  value: string | number
  accent?: 'brand' | 'success' | 'muted'
}) {
  return (
    <div className="text-center">
      <div className="rfid-qty-strip__label">{label}</div>
      <div
        className={cn(
          'rfid-qty-strip__value tabular-nums',
          accent === 'brand' && 'text-[var(--leap-brand)]',
          accent === 'success' && 'text-emerald-600',
          accent === 'muted' && 'text-muted-foreground'
        )}
      >
        {value}
      </div>
    </div>
  )
}

const PREVIEW_TAG_COUNT = 6

export function LineItemCard({
  materialCode,
  materialDesc,
  requiredQty,
  scanQty,
  nonScanQty,
  status: statusProp,
  scannable = true,
  fromBay = '',
  fromBin = '',
  toBay = '',
  showFromBay,
  showFromBin,
  showToBay,
  showShortQty,
  shortQty = 0,
  bayOptions = [],
  binOptions = [],
  onNonScanChange,
  onFromBayChange,
  onFromBinChange,
  onToBayChange,
  onShortQtyChange,
  tags = [],
  onRemoveTag,
  selected,
  onSelect,
  className,
}: LineItemCardProps) {
  const [tagsOpen, setTagsOpen] = useState(true)

  const filled = scanQty + nonScanQty
  const remaining = Math.max(0, requiredQty - filled)
  const progress =
    requiredQty > 0 ? Math.min(1, Math.max(0, filled / requiredQty)) : 0
  const scanPct =
    requiredQty > 0 ? Math.min(100, (scanQty / requiredQty) * 100) : 0
  const nonScanPct =
    requiredQty > 0 ? Math.min(100 - scanPct, (nonScanQty / requiredQty) * 100) : 0

  const status: LineScanStatus =
    statusProp ??
    (requiredQty > 0 && filled >= requiredQty
      ? 'complete'
      : filled > 0 || scanQty > 0
        ? 'in_progress'
        : 'pending')

  const complete = status === 'complete'
  const title = materialDesc?.trim() || materialCode || '—'
  const code = materialCode?.trim() || '—'
  const tagList = useMemo(() => tags.filter(Boolean), [tags])
  const previewTags = tagList.slice(0, PREVIEW_TAG_COUNT)
  const moreCount = Math.max(0, tagList.length - PREVIEW_TAG_COUNT)
  const statusMeta = STATUS_META[status]

  return (
    <div
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect()
              }
            }
          : undefined
      }
      className={cn(
        'rfid-line-card',
        selected && 'ring-2 ring-[var(--leap-brand)]/35 shadow-md',
        complete && 'border-emerald-200',
        onSelect && 'cursor-pointer',
        className
      )}
    >
      <div className="flex items-start gap-2">
        {complete ? (
          <CheckCircle2
            size={18}
            strokeWidth={2.4}
            className="mt-0.5 shrink-0 text-emerald-600"
          />
        ) : (
          <span
            className={cn(
              'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
              status === 'in_progress' ? 'bg-amber-500' : 'bg-muted-foreground'
            )}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[13px] font-extrabold leading-4 text-foreground line-clamp-2">
              {title}
            </div>
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide',
                statusMeta.className
              )}
            >
              {statusMeta.label}
            </span>
          </div>
          {materialDesc?.trim() && materialDesc.trim() !== code ? (
            <div className="mt-0.5 truncate font-mono text-[11px] font-semibold text-muted-foreground">
              {code}
            </div>
          ) : (
            <div className="mt-0.5 truncate font-mono text-[11px] font-semibold text-muted-foreground">
              {code}
            </div>
          )}
        </div>
        {scannable ? (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1"
            style={{
              borderColor: `${BRAND}33`,
              background: `${BRAND}12`,
              color: BRAND,
            }}
          >
            <Nfc size={16} strokeWidth={2.2} />
            <span className="text-[11px] font-extrabold tabular-nums">
              {tagList.length}
            </span>
          </span>
        ) : (
          <span className="rounded-lg border border-border bg-muted px-2 py-1 text-[10px] font-extrabold uppercase text-muted-foreground">
            Non-scan
          </span>
        )}
      </div>

      <div className="rfid-qty-strip rfid-qty-strip--4">
        <QtyCell label="Required" value={requiredQty} />
        <QtyCell label="Scanned" value={scanQty} accent="brand" />
        <QtyCell label="Non-scan" value={nonScanQty} />
        <QtyCell label="Remaining" value={remaining} accent={remaining > 0 ? 'muted' : 'success'} />
      </div>

      <div className="mt-2.5">
        <div className="mb-1 flex items-center justify-between text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
          <span>
            Fill {Math.round(progress * 100)}%
            {scannable ? ` · RFID ${Math.round(scanPct)}%` : null}
          </span>
          <span className="tabular-nums">
            {filled}/{requiredQty}
          </span>
        </div>
        <div className="flex h-2 overflow-hidden rounded-full bg-border">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${scanPct}%`, backgroundColor: BRAND }}
            title={`Scanned ${scanQty}`}
          />
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${nonScanPct}%`, backgroundColor: `${BRAND}55` }}
            title={`Non-scan ${nonScanQty}`}
          />
        </div>
        <div className="mt-1 flex gap-3 text-[10px] font-semibold text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: BRAND }} />
            RFID scan
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: `${BRAND}55` }} />
            Non-scan
          </span>
        </div>
      </div>

      {onNonScanChange ? (
        <div
          className="rfid-field mt-2.5 mb-0"
          onClick={(e) => e.stopPropagation()}
        >
          <label className="rfid-field__label" htmlFor={`non-scan-${materialCode}`}>
            Non-scan qty
          </label>
          <input
            id={`non-scan-${materialCode}`}
            type="number"
            min={0}
            className="rfid-field__control"
            value={nonScanQty}
            onChange={(e) => onNonScanChange(e.target.value)}
          />
        </div>
      ) : null}

      {(showFromBay || showFromBin || showToBay || showShortQty) && (
        <div
          className="mt-2 grid gap-2 sm:grid-cols-2"
          onClick={(e) => e.stopPropagation()}
        >
          {showFromBay ? (
            <div className="rfid-field mb-0">
              <label className="rfid-field__label" htmlFor={`from-bay-${materialCode}`}>
                From bay
              </label>
              <select
                id={`from-bay-${materialCode}`}
                className="rfid-field__control"
                value={fromBay}
                onChange={(e) => onFromBayChange?.(e.target.value)}
              >
                <option value="">Select bay</option>
                {bayOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {showFromBin ? (
            <div className="rfid-field mb-0">
              <label className="rfid-field__label" htmlFor={`from-bin-${materialCode}`}>
                From bin
              </label>
              <select
                id={`from-bin-${materialCode}`}
                className="rfid-field__control"
                value={fromBin}
                onChange={(e) => onFromBinChange?.(e.target.value)}
              >
                <option value="">Select bin</option>
                {binOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {showToBay ? (
            <div className="rfid-field mb-0">
              <label className="rfid-field__label" htmlFor={`to-bay-${materialCode}`}>
                To bay
              </label>
              <select
                id={`to-bay-${materialCode}`}
                className="rfid-field__control"
                value={toBay}
                onChange={(e) => onToBayChange?.(e.target.value)}
              >
                <option value="">Select bay</option>
                {bayOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {showShortQty ? (
            <div className="rfid-field mb-0">
              <label className="rfid-field__label" htmlFor={`short-qty-${materialCode}`}>
                Short qty
              </label>
              <input
                id={`short-qty-${materialCode}`}
                type="number"
                min={0}
                className="rfid-field__control"
                value={shortQty}
                onChange={(e) => onShortQtyChange?.(e.target.value)}
              />
            </div>
          ) : null}
        </div>
      )}

      {scannable ? (
        <div className="mt-3 border-t border-border pt-2.5" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            onClick={() => setTagsOpen((v) => !v)}
          >
            <span className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
              Scanned tags · {tagList.length}
            </span>
            {tagsOpen ? (
              <ChevronUp size={16} className="text-muted-foreground" />
            ) : (
              <ChevronDown size={16} className="text-muted-foreground" />
            )}
          </button>

          {tagsOpen ? (
            tagList.length === 0 ? (
              <p className="mt-2 rounded-[10px] border border-dashed border-border bg-muted/60 px-3 py-3 text-center text-[12px] font-semibold text-muted-foreground">
                No RFID tags allocated yet — scan or enter a tag ID
              </p>
            ) : (
              <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto pr-0.5">
                {previewTags.map((tag) => (
                  <li
                    key={tag}
                    className="flex items-center gap-2 rounded-[10px] border border-border bg-muted/40 px-2.5 py-1.5"
                  >
                    <Nfc size={14} className="shrink-0 text-[var(--leap-brand)]" />
                    <code className="min-w-0 flex-1 truncate font-mono text-[11px] font-bold text-foreground">
                      {tag}
                    </code>
                    {onRemoveTag ? (
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600"
                        aria-label={`Remove tag ${tag}`}
                        onClick={() => onRemoveTag(tag)}
                      >
                        <X size={14} strokeWidth={2.5} />
                      </button>
                    ) : null}
                  </li>
                ))}
                {moreCount > 0 ? (
                  <li>
                    <button
                      type="button"
                      className="w-full rounded-[10px] border border-dashed border-[var(--leap-brand)]/40 bg-[rgba(175, 0, 0,0.04)] px-3 py-2 text-left text-[11px] font-extrabold text-[var(--leap-brand)]"
                      onClick={() => onSelect?.()}
                    >
                      View all {tagList.length} tags →
                    </button>
                  </li>
                ) : null}
              </ul>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
